import { useCallback, useContext } from 'react';
import { NimContext, NimStatusContext } from 'components/Nim';
import { v4 as uuidV4 } from 'uuid';
import { overseaTranslate, TransFrom } from 'services/api/overseaTranslate';
import { useAuthorization } from 'components/Authorization';
import { restartTeam } from 'services/api/conversation';
import getImage from 'utils/getImage';
import { getTalkInfo, updateConversationV2 } from 'services/api/conversation.convList';
import dayjs from 'lib/dayjs';
import { clone, get, isString } from 'lodash-es';
import { isValidEmail } from 'utils/validator/email';
import { isURL } from 'utils/is';
import contentGenerator, {
  genBasicContentFrom,
  genCamlBasicContentFrom,
  ImageContent,
  ImageContnentExtra,
  ItemContent,
  OrderContent,
  StickerContent,
  TextContentExtra,
  VoucherContent,
  TextVideoContent,
  VideoContent,
  TemplateContent,
  AttachmentContent,
  ReturnRefundContent,
  TtsLogisticsCardContent,
} from './contentGenerator';
import getNimCacheId from './getNimCacheId';
import {
  getFakeConversationOrderSn,
  LocalMessage,
  LocalStatus,
  useMakeConversationCache,
} from '../conversation';
import { useSendingMsgMakeToConversation } from './useMsgToConversation';
import useRoles from '../useRoles';
import { useClearConverstaionInputDraft, useGetConversationDraftById } from '../conversationDraft';
import { eventBus } from 'services/eventBus';
import posthog from '@leyan/lytics';
import { useGetConversationById } from '../lruConversation';
import {
  useClearMsgAttachments,
  MsgAttachmentInfo,
} from 'views/Conversation/Chat/components/MessageEditor/Toolbar/AttachmentUpload/hooks/useUploadAttachment';
import { useMemoizedFn } from 'ahooks';
import { useRecordStickerToRecentUsed } from './sticker/recent';
import { getTextVideoTextContent } from '../sendMessage';
import { useGetConversationKnowledgeQueryMsgId } from './useGetConversationKnowledgeQueryMsgId';
import { useCheckQAPair } from './useCheckQAPair';
import { setSendTimestamp } from './qaPairTimestamp';
import { logger } from 'services/logger';
import { QAPairSupportMessageTypes } from './useQAPair';
import { useDeleteMessage, useGetMessageById, useUpdateMessage } from '../message';

const scene = 'team';
type ForceError = 'TRANSLATION_FAIL';

const getQuotedText = (msg?: Conversation.Message) => {
  if (!msg) {
    return false;
  }
  if (msg.messageType === 'FAQ_LIVEAGENT' || msg.messageType === 'TEXT') {
    return msg?.textContent?.text || false;
  }
  return false;
};

const createMessage = (
  conversation: Conversation.Conversation,
  assistantId: number,
  assistanNick: string,
  msgContent: Record<string, any>,
  quoteMsg?: Conversation.Message,
) => {
  // 历史遗留问题：
  // 以下omit的字段并不是不需要使用
  // 只是发送跟接收时使用的类型定义不一致，导致有类型冲突(string <-> number)
  // 暂时通过omit过滤
  const content: Partial<
    Omit<Conversation.Message, 'conversationId' | 'storeId' | 'fromId' | 'toId'>
  > = {
    ...genBasicContentFrom(conversation, assistantId, assistanNick),
    ...msgContent,
  };
  const messageToshow = {
    ...genCamlBasicContentFrom(conversation, assistantId, assistanNick),
    messageType: msgContent.msgType,
    imageContent: msgContent.imageContent,
    textContent: msgContent.textContent,
    itemContent: msgContent.itemContent,
    stickerContent: msgContent.stickerContent,
    orderContent: msgContent.orderContent,
    videoContent: msgContent.videoContent,
    textVideoContent: msgContent.textVideoContent,
    templateContent: msgContent.templateContent,
    attachmentContent: msgContent.attachmentContent,
    fbMsgContent: msgContent.fbMsgContent,
    ttsLogisticsCardContent: msgContent.ttsLogisticsCardContent,
    jlAttachmentContent: msgContent.jlAttachmentContent,
    localStatus: 'loading',
    voucher: msgContent.voucher,
    replyId: msgContent.replyId,
    extra: msgContent.extra || '',
  } as LocalMessage;
  const quotedText = getQuotedText(quoteMsg);
  if (quoteMsg && quotedText && messageToshow.messageType === 'TEXT') {
    messageToshow.quotedTextContent = { text: quotedText };
    messageToshow.quotedMsgId = quoteMsg.msgId;
    content.quotedTextContent = { text: quotedText };
    content.quotedMsgId = quoteMsg.msgId;
  }
  return {
    content,
    messageToshow,
  };
};

const imageContentPreivewFilter = (m: any) => {
  if (m?.imageContent?.imgUrl && !/^https?:/.test(m?.imageContent?.imgUrl)) {
    // eslint-disable-next-line no-param-reassign
    m.imageContent = { ...m.imageContent, imgUrl: '' };
  }
  if (m?.image_content?.imgUrl && !/^https?:/.test(m?.image_content?.imgUrl)) {
    // eslint-disable-next-line no-param-reassign
    m.image_content = { ...m.image_content, imgUrl: '' };
  }
  return m;
};

const getTranslation = async (
  text: string,
  lang: General.Language,
  from: TransFrom,
  region?: General.UpperCaseRegion,
  platform?: General.PlatformUpperCaseTunnel,
): Promise<string> => {
  if (isValidEmail(text.trim()) || isURL(text.trim())) {
    return text;
  }
  const result = await overseaTranslate(text, lang, from, region, platform).catch((e) => {
    // eslint-disable-next-line no-console
    console.log('auto translate failed at send out', e);
    return '';
  });
  return result;
};

export const shouldAcquireControlConversationMapRef = {
  ref: {} as Partial<Record<string, Conversation.ForceAcquireControlReason>>,
};

const getAutoTranslationInfo = async (
  text: string,
  region: Conversation.Message['region'],
  autoTranslationSource?: TextContentExtra['autoTranslation'],
  platform?: General.PlatformUpperCaseTunnel,
) => {
  let sendToBuyerText = text;
  let forceError;
  let autoTranslation = autoTranslationSource;

  if (autoTranslationSource) {
    const { status: initStatus, text: translationText, ...translation } = autoTranslationSource;
    const translateResult =
      translationText ||
      (await getTranslation(
        text,
        autoTranslationSource.targetLanguage,
        'messageEditor',
        region,
        platform,
      ));

    const status = translateResult ? 'success' : 'error';

    sendToBuyerText = translateResult;
    // 翻译出错以后强制发送失败
    if (!translateResult) {
      forceError = 'TRANSLATION_FAIL' as const;
    }

    autoTranslation = {
      ...translation,
      status,
      source: 'sender',
      text: translateResult,
    };
  }
  return {
    sendToBuyerText,
    forceError,
    autoTranslation,
  };
};

const getFacebookMessageContent = (messageContent: any) => {
  const { messageType, textContent, imageContent, videoContent, ...others } = messageContent;
  if (messageType === 'TEXT') {
    return {
      ...others,
      messageType: 'FACEBOOK_MESSAGE',
      fbMsgContent: {
        message: textContent?.text,
      } as any,
    };
  } else if (messageType === 'IMAGE') {
    return {
      ...others,
      messageType: 'FACEBOOK_MESSAGE',
      fbMsgContent: {
        attachments: {
          data: [
            {
              id: imageContent?.externalId,
              mimeType: imageContent?.mimeType,
              imageData: {
                url: imageContent?.imgUrl,
                width: imageContent?.width,
                height: imageContent?.height,
              },
            },
          ],
        },
      } as any,
    };
  } else if (messageType === 'VIDEO') {
    return {
      ...others,
      messageType: 'FACEBOOK_MESSAGE',
      fbMsgContent: {
        attachments: {
          data: [
            {
              id: videoContent?.externalId,
              mimeType: videoContent?.mimeType,
              videoData: {
                previewUrl: videoContent?.thumbUrl,
                url: videoContent?.localVideoUrl,
              },
            },
          ],
        },
      } as any,
    };
  }
  return messageContent;
};

const useIM = () => {
  const im = useContext(NimContext);
  const auth = useAuthorization<Oversea.Token.Payload>();
  const assistantId = auth?.payload?.id;
  const assistanNick = auth?.payload?.name || '';
  const { send } = im;
  const { isSuperviser } = useRoles();

  const getMessageById = useGetMessageById();
  const updateMessage = useUpdateMessage();
  const deleteMessage = useDeleteMessage();
  const recordStickerToRecentUsed = useRecordStickerToRecentUsed();
  const getConversationDraftById = useGetConversationDraftById();
  const clearDraft = useClearConverstaionInputDraft();
  const clearMsgAttachments = useClearMsgAttachments();
  const { insertQAPairMsg, replaceQAPairMsg } = useCheckQAPair();
  const getConversationKnowledgeQueryMsgId = useGetConversationKnowledgeQueryMsgId();
  const getConversationById = useGetConversationById();

  const createLocalMessage = useMemoizedFn(
    async (
      conversationId: Conversation.Conversation['conversationId'],
      msgContent: Record<string, any>,
      localStatus: LocalMessage['localStatus'] = 'loading',
      uuid: string = uuidV4(),
    ) => {
      const conversation = getConversationById(conversationId);
      if (!conversation || !assistantId) {
        return false;
      }

      const forceAcquireControlReason = shouldAcquireControlConversationMapRef.ref[conversationId];

      if (forceAcquireControlReason === 'PROCESS_TODO') {
        updateConversationV2({
          conversationId,
          userId: `${assistantId}`,
          sourceAction: 'TODO',
        });
      }

      const draft = await getConversationDraftById(conversationId);
      const quotedMessageId = draft?.quotedMessageId;
      const quotedMessage = quotedMessageId ? getMessageById(quotedMessageId) : undefined;
      const { messageToshow, content: contentToSend } = createMessage(
        conversation,
        assistantId,
        assistanNick,
        msgContent,
        quotedMessage,
      );
      const tempLoacalId = getNimCacheId(uuid);
      messageToshow.yunXinMsgId = uuid;
      messageToshow.msgId = tempLoacalId;
      messageToshow.externalId = tempLoacalId;
      messageToshow.sendTime =
        conversation.channel === 'SHOPEE' ? dayjs().millisecond(0).valueOf() : dayjs().valueOf();
      messageToshow.localStatus = localStatus;
      updateMessage(tempLoacalId, messageToshow);

      return {
        uuid,
        messageToshow,
        contentToSend,
      };
    },
  );
  const setConversation = useMakeConversationCache('set');
  const sendingMsgMakeToConversation = useSendingMsgMakeToConversation();

  const sendToConversation = useMemoizedFn(
    async (
      conversationId: Conversation.Conversation['conversationId'],
      msgContent: Record<string, any>,
      uuid: string = uuidV4(),
      forceError?: ForceError,
    ): Promise<false | LocalMessage> => {
      const conversation = getConversationById(conversationId);

      if (!conversation || !assistantId) {
        return false;
      }
      let failCause: Conversation.FailContent | undefined;
      const { content, messageToshow } = createMessage(
        conversation,
        assistantId,
        assistanNick,
        msgContent,
      );
      const tempLoacalId = getNimCacheId(uuid);

      // QAPair 标记处理
      if (QAPairSupportMessageTypes.includes(messageToshow.messageType)) {
        try {
          const knowledgeQueryMsgId = getConversationKnowledgeQueryMsgId(conversationId);
          if (knowledgeQueryMsgId) {
            let tempExtra = content.extra;
            if (tempExtra) {
              if (isString(tempExtra)) {
                tempExtra = JSON.stringify({
                  ...JSON.parse(tempExtra as string),
                  popupFor: knowledgeQueryMsgId,
                });
              } else {
                tempExtra = JSON.stringify({
                  ...(tempExtra as Record<string, any>),
                  popupFor: knowledgeQueryMsgId,
                });
              }
            } else {
              tempExtra = JSON.stringify({
                popupFor: knowledgeQueryMsgId,
              });
            }
            content.extra = tempExtra;
            messageToshow.extra = tempExtra;
            messageToshow.extraInfo = JSON.parse(tempExtra);
          }
        } catch (err: any) {
          logger.info(
            `[useIM][sendToConversation][getConversationKnowledgeQueryMsgId]conversationId: ${
              conversation.conversationId
            }, getConversationKnowledgeQueryMsgId error: ${err?.message || ''}`,
          );
        }
      }

      let hasTryRestartTeam = false;
      const sendToYunXinContent = imageContentPreivewFilter({
        ...content,
        ...messageToshow,
        isSupervise: isSuperviser,
        // 构造的本地msgId不应该传递到线上,这样会导致数据混乱(群聊中多帐号互通的时候ack无法命中)
        msgId: undefined,
        externalId: undefined,
      });
      if (conversation.mockExternal) {
        const orderSn = getFakeConversationOrderSn(conversationId);
        sendToYunXinContent.sourceContent = {
          sourceContentType: 2,
          orderSn,
        };
        posthog.capture('$sourceContent', sendToYunXinContent);
      }
      const sendMsg = async (
        content: LocalMessage,
        _conversation = conversation,
      ): Promise<false | LocalMessage> => {
        const messageToSend = {
          ...messageToshow,
          ...content,
          yunXinMsgId: uuid,
          msgId: tempLoacalId,
          externalId: tempLoacalId,
          sendTime:
            conversation.channel === 'SHOPEE'
              ? dayjs().millisecond(0).valueOf()
              : dayjs().valueOf(),
          localStatus: 'loading' as LocalStatus,
        };
        updateMessage(tempLoacalId, clone(messageToSend));
        try {
          const sendContent =
            conversation.channel === 'FACEBOOK'
              ? getFacebookMessageContent(messageToSend)
              : messageToSend;

          try {
            await insertQAPairMsg(sendContent);
          } catch (err: any) {
            logger.info(
              `[useIM][sendToConversation][insertQAPairMsg]conversationId: ${
                conversation.conversationId
              }, insertQAPairMsg error: ${err?.message || ''}`,
            );
          }

          const resp = await send({
            to: _conversation.yunXinTid,
            scene,
            content: sendContent,
          });
          const { idServer } = resp;
          if (!idServer) return false;
          messageToSend.yunXinMsgId = idServer;
          const nimId = getNimCacheId(idServer);
          messageToSend.msgId = nimId;
          messageToSend.externalId = nimId;
          // messageToshow.sendTime = userUpdateTime;
          messageToSend.localStatus = 'processing' as LocalStatus;
          updateMessage(messageToSend.msgId, clone(messageToSend));
          deleteMessage(tempLoacalId);

          requestAnimationFrame(() => {
            try {
              replaceQAPairMsg(tempLoacalId, clone(messageToSend));
            } catch (err: any) {
              logger.info(
                `[useIM][sendToConversation][replaceQAPairMsg]conversationId: ${
                  conversation.conversationId
                }, replaceQAPairMsg error: ${err?.message || ''}`,
              );
            }
          });

          sendingMsgMakeToConversation(messageToSend);
          return messageToSend;
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log('sendErr', e);
          // NIMError: sendTeamMsg error: 没有权限
          const msg = (e as Error).message;
          logger.info(
            `[useIM][sendToConversation]conversationId: ${
              conversation.conversationId
            }, send error: ${msg || ''}, failCause: ${failCause ? JSON.stringify(failCause) : ''}`,
          );
          if (msg && msg.includes('NIMError:')) {
            const errorArr = msg.split(':');
            if (['socket状态不对', '超时', '网断了'].includes(errorArr[1]?.trim())) {
              failCause = {
                failType: 'A',
                code: 'network_error',
                desc: '网络错误',
                failMsgMap: {
                  'zh-CN': '网络连接不可用，请稍后尝试重新发送',
                  'en-US': 'The network connection is unavailable. Please try sending again later.',
                },
              };
            } else {
              try {
                if (!hasTryRestartTeam) {
                  let convInfo: Conversation.Conversation | undefined;
                  await restartTeam({
                    conversationId: conversation.conversationId,
                    errorMsg: (errorArr.pop() as string).trim(),
                  })
                    .catch(() => {})
                    .finally(() => {
                      return getTalkInfo({
                        conversationId: conversation.conversationId,
                      })
                        .then((res) => {
                          setConversation(res);
                          convInfo = res;
                        })
                        .catch(() => {});
                    });
                  hasTryRestartTeam = true;
                  return sendMsg(sendToYunXinContent, convInfo);
                }

                const convInfo = await getTalkInfo({
                  conversationId: conversation.conversationId,
                });
                setConversation(convInfo);
              } catch (e: any) {
                posthog.capture('$restartTeamError', {
                  errString: JSON.stringify(e?.response?.data || ''),
                });
                if (
                  !JSON.stringify(e?.response?.data || '').includes('RESTART_TEAM_OTHER_RECEPTiON')
                ) {
                  failCause = {
                    failType: 'A',
                    code: 'network_error',
                    desc: '网络错误',
                    failMsgMap: {
                      'zh-CN': '网络连接不可用，请稍后尝试重新发送',
                      'en-US':
                        'The network connection is unavailable. Please try sending again later.',
                    },
                  };
                }
              }
            }
          }
          updateMessage(tempLoacalId, {
            ...clone(messageToSend),
            localStatus: 'error',
            resendContext: {
              doResend: sendMsg,
              content: {
                ...sendToYunXinContent,
                sendTime: messageToSend.sendTime,
              },
            },
            failCause,
          });
          sendingMsgMakeToConversation(messageToSend, failCause);
          return false;
        }
      };
      if (forceError === 'TRANSLATION_FAIL') {
        failCause = {
          failType: 'A',
          code: 'translation_error',
          desc: '翻译错误',
          failMsgMap: {
            'zh-CN': '翻译出错，请重新翻译',
            'en-US': 'Translation error, please translate again.',
          },
        };

        messageToshow.yunXinMsgId = uuid;
        messageToshow.msgId = tempLoacalId;
        messageToshow.externalId = tempLoacalId;
        messageToshow.sendTime = new Date().valueOf();
        messageToshow.localStatus = 'error';

        updateMessage(tempLoacalId, {
          ...clone(messageToshow),
          resendContext: {
            doResend: sendMsg,
            content: {
              ...sendToYunXinContent,
              sendTime: messageToshow.sendTime,
            },
          },
          failCause,
        });
        sendingMsgMakeToConversation(messageToshow, failCause);
      } else {
        return await sendMsg(sendToYunXinContent);
      }

      return false;
    },
  );

  const listenCancelMessage = useCallback(
    (msgId: string) => {
      const cancelledRef = {
        current: false,
      };

      const handleCancelSendMessage = (message: Conversation.Message) => {
        if (message.msgId !== msgId) {
          return;
        }
        deleteMessage(msgId);
        off();
        cancelledRef.current = true;
      };

      eventBus.on('CANCEL_SEND_MESSAGE', handleCancelSendMessage);

      const off = () => eventBus.off('CANCEL_SEND_MESSAGE', handleCancelSendMessage);

      return {
        cancelledRef,
        off,
      };
    },
    [deleteMessage],
  );

  const sendText = useMemoizedFn(
    async (
      conversationId: Conversation.Conversation['conversationId'],
      text: string,
      extra?: TextContentExtra,
    ): Promise<LocalMessage | false> => {
      const messageContent = contentGenerator('TEXT', text, extra);

      const localMessage = await createLocalMessage(conversationId, messageContent);
      if (!localMessage) {
        return false;
      }
      // 创建完待发送消息后，清除文本框草稿缓存
      setSendTimestamp(conversationId, Date.now());
      clearDraft(conversationId);
      const { sendToBuyerText, forceError, autoTranslation } = await getAutoTranslationInfo(
        text,
        localMessage.messageToshow.region,
        extra?.autoTranslation,
        localMessage.messageToshow.channel,
      );

      const { uuid, messageToshow: { quotedMsgId, quotedTextContent } = {} } = localMessage;
      return sendToConversation(
        conversationId,
        {
          ...localMessage.contentToSend,
          quotedMsgId,
          quotedTextContent,
          textContent: {
            ...localMessage.contentToSend.textContent,
            text: sendToBuyerText,
            autoTranslation,
          },
        },
        uuid,
        forceError,
      ).catch(() => {
        return false;
      });
    },
  );

  const sendSticker = useMemoizedFn(
    (
      conversationId: Conversation.Conversation['conversationId'],
      data: StickerContent['stickerContent'],
      raw?: Conversation.Sticker,
    ) => {
      if (raw) {
        // 记录常用表情
        recordStickerToRecentUsed(raw.id);
      }
      return sendToConversation(conversationId, contentGenerator('STICKER', data));
    },
  );

  const sendItem = useMemoizedFn(
    (
      conversationId: Conversation.Conversation['conversationId'],
      data: ItemContent['itemContent'],
    ) => {
      return sendToConversation(conversationId, contentGenerator('ITEM', data));
    },
  );

  const sendImage = useMemoizedFn(
    async (
      conversationId: Conversation.Conversation['conversationId'],
      data: ImageContent['imageContent'] | Promise<ImageContent['imageContent']>,
      preview?: string,
      extra?: ImageContnentExtra,
      uid?: string,
    ) => {
      let uuid: string | undefined;
      if (preview) {
        // not promise
        const { width, height } = await getImage(preview);

        const tempInfo = await createLocalMessage(
          conversationId,
          contentGenerator(
            'IMAGE',
            {
              externalImgUrl: preview,
              imgUrl: preview,
              width,
              height,
            },
            extra,
          ),
          'uploading',
          uid,
        );
        if (!tempInfo) {
          return false;
        }
        uuid = tempInfo.uuid;
      }

      const msgId = `nim-${uuid}`;
      setSendTimestamp(conversationId, Date.now());

      const { cancelledRef, off } = listenCancelMessage(msgId);

      try {
        const imageData = await Promise.resolve(data).catch((e) => {
          if (preview) {
            const failCause = get(e, 'failCause');

            if (failCause) {
              const id = `nim-${uuid}`;

              updateMessage(id, (prev) => {
                if (prev) {
                  return {
                    ...prev,
                    status: 'FAIL',
                    localStatus: 'error',
                    failCause,
                  };
                }
              });
            } else {
              createLocalMessage(
                conversationId,
                contentGenerator(
                  'IMAGE',
                  {
                    externalImgUrl: preview,
                    imgUrl: preview,
                    width: 0, // 创建本地消息，不需要准确的宽高信息
                    height: 0,
                  },
                  extra,
                ),
                'error',
                uuid,
              );
            }
          }
          throw e;
        });

        off();

        if (cancelledRef.current) {
          return false;
        }

        return sendToConversation(
          conversationId,
          contentGenerator('IMAGE', imageData, extra),
          uuid,
        );
      } catch (e) {
        off();

        return false;
      }
    },
  );

  const sendOrder = useMemoizedFn(
    (
      conversationId: Conversation.Conversation['conversationId'],
      data: OrderContent['orderContent'],
    ) => {
      return sendToConversation(conversationId, contentGenerator('ORDER', data));
    },
  );
  const sendFollower = useMemoizedFn(
    (conversationId: Conversation.Conversation['conversationId']) => {
      return sendToConversation(conversationId, contentGenerator('FOLLOWER'));
    },
  );
  const sendVoucher = useMemoizedFn(
    (
      conversationId: Conversation.Conversation['conversationId'],
      data: VoucherContent['voucherContent'],
    ) => {
      return sendToConversation(conversationId, contentGenerator('VOUCHER', data));
    },
  );

  const sendTextVideo = useMemoizedFn(
    async (
      conversationId: Conversation.Conversation['conversationId'],
      data: TextVideoContent['textVideoContent'] | Promise<TextVideoContent['textVideoContent']>,
      preview: string = '',
    ) => {
      const tempData = {
        externalImgUrl: preview,
        imgUrl: preview,
        width: 0,
        height: 0,
      };

      const tempInfo = await createLocalMessage(
        conversationId,
        contentGenerator('IMAGE', tempData),
        'uploading',
      );

      if (!tempInfo) {
        return false;
      }

      const { uuid } = tempInfo;

      const msgId = `nim-${uuid}`;
      setSendTimestamp(conversationId, Date.now());

      const { cancelledRef, off } = listenCancelMessage(msgId);

      try {
        const textVideoData = await Promise.resolve(data).catch((e) => {
          const failCause = get(e, 'failCause');

          if (failCause) {
            const id = `nim-${uuid}`;
            updateMessage(id, (prev) => {
              if (prev) {
                return {
                  ...prev,
                  status: 'FAIL',
                  localStatus: 'error',
                  failCause,
                };
              }
            });
          } else {
            createLocalMessage(conversationId, contentGenerator('IMAGE', tempData), 'error', uuid);
          }

          throw e;
        });

        off();

        if (cancelledRef.current) {
          return false;
        }

        return sendToConversation(
          conversationId,
          contentGenerator('TEXT_VIDEO', textVideoData),
          uuid,
        );
      } catch (e) {
        off();

        return false;
      }
    },
  );

  const sendVideo = useMemoizedFn(
    async (
      conversationId: Conversation.Conversation['conversationId'],
      data: VideoContent['videoContent'] | Promise<VideoContent['videoContent']>,
      preview: string = '',
    ) => {
      const tempData = {
        externalImgUrl: preview,
        imgUrl: preview,
        width: 0,
        height: 0,
      };

      const tempInfo = await createLocalMessage(
        conversationId,
        contentGenerator('IMAGE', tempData),
        'uploading',
      );

      if (!tempInfo) {
        return false;
      }

      const { uuid } = tempInfo;

      const msgId = `nim-${uuid}`;
      setSendTimestamp(conversationId, Date.now());

      const { cancelledRef, off } = listenCancelMessage(msgId);

      try {
        const videoData = await Promise.resolve(data).catch((e) => {
          const failCause = get(e, 'failCause');

          if (failCause) {
            updateMessage(msgId, (prev) => {
              if (prev) {
                return {
                  ...prev,
                  status: 'FAIL',
                  localStatus: 'error',
                  failCause,
                };
              }
            });
          } else {
            createLocalMessage(conversationId, contentGenerator('IMAGE', tempData), 'error', uuid);
          }

          throw e;
        });

        off();

        if (cancelledRef.current) {
          return false;
        }

        const { extra, ...restVideoData } = videoData as any;

        return sendToConversation(
          conversationId,
          contentGenerator('VIDEO', restVideoData, extra),
          uuid,
        );
      } catch (e) {
        off();

        return false;
      }
    },
  );

  const sendTemplate = useMemoizedFn(
    async (
      conversationId: Conversation.Conversation['conversationId'],
      data: TemplateContent['templateContent'],
    ) => {
      return sendToConversation(conversationId, contentGenerator('TEMPLATE', data));
    },
  );

  const sendAttachment = useMemoizedFn(
    async (
      conversation: Conversation.Conversation,
      data: AttachmentContent['attachmentContent'],
      extra: AttachmentContent['autoTranslation'],
      onMercadoChatView: boolean,
    ) => {
      const { conversationId } = conversation;
      const msgContent = contentGenerator('ATTACHMENT', data);
      const localMessage = await createLocalMessage(conversationId, {
        ...msgContent,
        msgFromType: onMercadoChatView ? 'ASSISTANT2MERCADO' : 'ASSISTANT',
      });
      if (!localMessage) {
        return false;
      }
      // 创建完待发送消息后，清除文本框草稿缓存
      clearDraft(conversationId);
      clearMsgAttachments(conversationId);
      const { uuid, contentToSend } = localMessage;
      const msgFromType = onMercadoChatView ? 'ASSISTANT2MERCADO' : 'ASSISTANT';

      // 支持只发送附件
      if (data.text) {
        const { sendToBuyerText, forceError, autoTranslation } = await getAutoTranslationInfo(
          data.text,
          localMessage.messageToshow.region,
          extra,
          conversation.channel,
        );

        return sendToConversation(
          conversationId,
          {
            ...contentToSend,
            msgFromType,
            mediation: onMercadoChatView,
            withMercado: onMercadoChatView,
            tunnelType: 'MERCADO',
            attachmentContent: {
              ...contentToSend.attachmentContent,
              text: sendToBuyerText,
              autoTranslation,
            },
          },
          uuid,
          forceError,
        );
      } else {
        return sendToConversation(
          conversationId,
          {
            ...contentToSend,
            msgFromType,
            withMercado: onMercadoChatView,
          },
          uuid,
        );
      }
    },
  );

  const sendJolectAttachment = useMemoizedFn(
    (conversation: Conversation.Conversation, attachment: MsgAttachmentInfo) => {
      const { data, metadata } = attachment;
      const { url, originalFilename: name, size, type: mimeType } = data;

      if (mimeType?.startsWith('image/') && metadata?.width && metadata?.height) {
        return sendToConversation(
          conversation.conversationId,
          contentGenerator('IMAGE', {
            imgUrl: url,
            externalImgUrl: data.filename || url,
            width: metadata.width,
            height: metadata.height,
            mimeType,
          }),
        );
      }

      if (mimeType?.startsWith('video/')) {
        return sendToConversation(
          conversation.conversationId,
          contentGenerator('VIDEO', {
            videoUrl: url,
            localVideoUrl: url,
            thumbUrl: metadata?.thumbUrl || '',
            thumbWidth: metadata?.thumbWidth,
            thumbHeight: metadata?.thumbHeight,
            durationSeconds: metadata?.durationSeconds,
            videoName: name,
            videoId: data.filename || '',
            mimeType,
          }),
        );
      }

      return sendToConversation(
        conversation.conversationId,
        contentGenerator('JL_ATTACHMENT', {
          url,
          name,
          size,
          mimeType,
        }),
      );
    },
  );

  const sendReturnRefundCard = useMemoizedFn(
    (
      conversationId: Conversation.Conversation['conversationId'],
      data: ReturnRefundContent['returnRefundCardContent'],
    ) => {
      return sendToConversation(conversationId, contentGenerator('RETURN_REFUND_CARD', data));
    },
  );

  const sendShopeeVideo = useMemoizedFn(
    async (
      conversationId: Conversation.Conversation['conversationId'],
      data: Promise<Conversation.MsgTextVideo['videoContent'] & { contentLength: number }>,
      preview: string = '',
      targetLang?: General.Language,
    ) => {
      const tempData = {
        externalImgUrl: preview,
        imgUrl: preview,
        width: 0,
        height: 0,
      };

      const tempInfo = await createLocalMessage(
        conversationId,
        contentGenerator('IMAGE', tempData),
        'uploading',
      );

      if (!tempInfo) {
        return false;
      }

      const { uuid } = tempInfo;

      const msgId = `nim-${uuid}`;
      setSendTimestamp(conversationId, Date.now());

      const { cancelledRef, off } = listenCancelMessage(msgId);

      try {
        const videoContent = await Promise.resolve(data).catch((e) => {
          const failCause = get(e, 'failCause');

          if (failCause) {
            updateMessage(msgId, (prev) => {
              if (prev) {
                return {
                  ...prev,
                  status: 'FAIL',
                  localStatus: 'error',
                  failCause,
                };
              }
            });
          } else {
            createLocalMessage(conversationId, contentGenerator('IMAGE', tempData), 'error', uuid);
          }

          throw e;
        });

        off();

        if (cancelledRef.current) {
          return false;
        }
        if (videoContent.vid) {
          const {
            thumbUrl,
            videoUrl,
            thumbWidth,
            thumbHeight,
            localVideoUrl,
            durationSeconds,
            resourceId,
            vid,
            contentLength,
          } = videoContent;
          const videoData = {
            videoUrl,
            thumbUrl,
            thumbWidth,
            thumbHeight,
            localVideoUrl,
            contentLength,
            durationSeconds,
            resourceId,
            // shopee 的 videoId 必须为空
            videoId: '',
            vid,
          };
          return sendToConversation(conversationId, contentGenerator('VIDEO', videoData), uuid);
        } else {
          const textVideoData = {
            videoContent,
            textContent: getTextVideoTextContent(videoContent.videoUrl, targetLang || 'en-US'),
          };
          return sendToConversation(
            conversationId,
            contentGenerator('TEXT_VIDEO', textVideoData),
            uuid,
          );
        }
      } catch (e) {
        off();

        return false;
      }
    },
  );

  interface FileData {
    id?: string;
    mimeType?: string;
    name: string;
    size?: number;
    fileUrl: string;
  }

  const sendFile = useMemoizedFn(
    async (
      conversationId: Conversation.Conversation['conversationId'],
      data: FileData | Promise<FileData>,
      preview?: Partial<FileData>,
      uid?: string,
    ) => {
      let uuid: string | undefined;
      if (preview) {
        const tempInfo = await createLocalMessage(
          conversationId,
          contentGenerator('FACEBOOK_MESSAGE', {
            attachments: {
              data: [preview],
            },
          }),
          'uploading',
          uid,
        );
        if (!tempInfo) {
          return false;
        }
        uuid = tempInfo.uuid;
      }

      const msgId = `nim-${uuid}`;

      const { cancelledRef, off } = listenCancelMessage(msgId);

      try {
        const fileData = await Promise.resolve(data).catch((e) => {
          if (preview) {
            const failCause = get(e, 'failCause');

            if (failCause) {
              const id = `nim-${uuid}`;

              updateMessage(id, (prev) => {
                if (prev) {
                  return {
                    ...prev,
                    status: 'FAIL',
                    localStatus: 'error',
                    failCause,
                  };
                }
              });
            } else {
              createLocalMessage(
                conversationId,
                contentGenerator('FACEBOOK_MESSAGE', {
                  attachments: {
                    data: [preview],
                  },
                }),
                'error',
                uuid,
              );
            }
          }
          throw e;
        });

        off();

        if (cancelledRef.current) {
          return false;
        }

        return sendToConversation(
          conversationId,
          contentGenerator('FACEBOOK_MESSAGE', {
            attachments: {
              data: [fileData],
            },
          }),
          uuid,
        );
      } catch (e) {
        off();

        return false;
      }
    },
  );

  const sendTtsLogisticsCard = useMemoizedFn(
    (
      conversationId: Conversation.Conversation['conversationId'],
      data: TtsLogisticsCardContent['ttsLogisticsCardContent'],
    ) => {
      posthog.capture('$sendTtsLogisticsCard');
      return sendToConversation(conversationId, contentGenerator('TTS_LOGISTICS_CARD', data));
    },
  );

  return {
    ...im,
    sendText,
    sendSticker,
    sendItem,
    sendImage,
    sendOrder,
    sendFollower,
    sendVoucher,
    sendTextVideo,
    sendVideo,
    sendTemplate,
    sendAttachment,
    sendJolectAttachment,
    sendReturnRefundCard,
    sendShopeeVideo,
    sendFile,
    sendTtsLogisticsCard,
  };
};

export const useIMStatus = () => useContext(NimStatusContext);
export default useIM;
